import { h } from "./dom";
import { Spring, drag, nearest, prefersReducedMotion, project, rubberband } from "./motion";

export type IconName = "today" | "log" | "guide" | "report" | "settings" | "plus";

export interface TabSpec {
  id: string;
  label: string;
  icon: IconName;
  /** The middle button. It is an action, not a destination. */
  center?: boolean;
}

const ICON_PATHS: Record<IconName, string> = {
  today: '<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/>',
  log: '<path d="M4 6h16M4 12h16M4 18h10"/>',
  guide: '<circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 3.6-7 8-7s8 3 8 7"/>',
  report: '<path d="M6 3h8l4 4v14H6z"/><path d="M14 3v4h4M9 12h6M9 16h6"/>',
  settings: '<path d="M4 7h10M18 7h2M4 17h4M12 17h8"/><circle cx="16" cy="7" r="2"/><circle cx="10" cy="17" r="2"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
};

export function icon(name: IconName): SVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  svg.innerHTML = ICON_PATHS[name];
  return svg;
}

export interface ShellOptions {
  initial?: string;
  /** Called when the centre button is pressed. */
  onCenter?: () => void;
  /** Called after a destination changes, with the new id. */
  onChange?: (id: string) => void;
}

export interface Shell {
  readonly root: HTMLElement;
  readonly content: HTMLElement;
  readonly bar: HTMLElement;
  readonly active: string;
  go(id: string): void;
  /** Rebuilds the current page, for example after the record changed. */
  render(): void;
  button(id: string): HTMLButtonElement;
}

/**
 * The parent app frame: one page at a time above a five-button tab bar. A tab
 * swap is instant on purpose. It happens tens of times a day and any motion
 * would only make it slower.
 */
export function createShell(
  root: HTMLElement,
  tabs: readonly TabSpec[],
  pages: Record<string, () => HTMLElement>,
  options: ShellOptions = {},
): Shell {
  const content = h("div", { class: "shell-content" });
  const bar = h("nav", { class: "tabbar", "aria-label": "Sections" });
  const buttons = new Map<string, HTMLButtonElement>();
  let active = options.initial ?? tabs.find((t) => !t.center)?.id ?? "";

  for (const tab of tabs) {
    const button = h(
      "button",
      {
        type: "button",
        id: `tab-${tab.id}`,
        class: tab.center ? "tab center" : "tab",
        "aria-label": tab.center ? tab.label : null,
        onclick: () => {
          if (tab.center) {
            options.onCenter?.();
            return;
          }
          if (tab.id === active) return;
          go(tab.id);
        },
      },
      h("span", {}, icon(tab.icon), !tab.center && h("span", {}, tab.label)),
    );
    buttons.set(tab.id, button);
    bar.appendChild(button);
  }

  root.classList.add("shell");
  root.replaceChildren(content, bar);

  function render(): void {
    const page = pages[active];
    if (!page) throw new Error(`No page for tab ${active}`);
    content.replaceChildren(page());
    content.scrollTop = 0;
  }

  function go(id: string): void {
    active = id;
    for (const [tabId, button] of buttons) {
      if (tabId === id) button.setAttribute("aria-current", "page");
      else button.removeAttribute("aria-current");
    }
    render();
    options.onChange?.(id);
  }

  go(active);

  return {
    root,
    content,
    bar,
    get active() {
      return active;
    },
    go,
    render,
    button(id: string): HTMLButtonElement {
      const button = buttons.get(id);
      if (!button) throw new Error(`No tab ${id}`);
      return button;
    },
  };
}

export interface ComposeOptions {
  /** The layer that pushes back while the surface is open. */
  pushBack: HTMLElement;
  onOpen?: () => void;
  onClose?: () => void;
}

export interface Compose {
  readonly el: HTMLElement;
  readonly isOpen: boolean;
  open(): void;
  close(): void;
  dispose(): void;
}

const CLOSED_SCALE = 0.35;
const RESPONSE_OPEN = 0.4;

/**
 * A surface that grows out of the button that asked for it and shrinks back
 * the same way. Progress is one spring from 0 (home) to 1 (open). A swipe down
 * on the header follows the finger and a release goes where it was heading.
 */
export function createCompose(
  app: HTMLElement,
  anchor: HTMLElement,
  surface: HTMLElement,
  dragSurfaces: readonly HTMLElement[],
  options: ComposeOptions,
): Compose {
  const reduced = prefersReducedMotion();
  const scrim = h("div", { class: "scrim" });
  app.append(scrim, surface);
  surface.hidden = true;
  const cleanups: Array<() => void> = [];
  let open = false;

  const apply = (value: number): void => {
    const p = Math.max(0, Math.min(1.08, value));
    scrim.style.opacity = (p * 0.25).toFixed(3);
    scrim.classList.toggle("on", p > 0.5);
    anchor.setAttribute("aria-expanded", String(p > 0.5));
    if (reduced) {
      surface.style.opacity = String(Math.min(1, p));
      return;
    }
    surface.style.transform = `scale(${(CLOSED_SCALE + (1 - CLOSED_SCALE) * p).toFixed(4)})`;
    surface.style.opacity = String(Math.min(1, p * 2.2));
    options.pushBack.style.transform = `scale(${(1 - 0.03 * p).toFixed(4)})`;
  };

  const progress = new Spring(0, {
    response: RESPONSE_OPEN,
    damping: 1,
    precision: 0.002,
    restVelocity: 0.01,
    onUpdate: apply,
    onRest: () => {
      if (progress.value === 0) {
        surface.hidden = true;
        options.onClose?.();
      }
    },
  });

  function openSurface(): void {
    const a = app.getBoundingClientRect();
    const b = anchor.getBoundingClientRect();
    surface.style.transformOrigin = `${b.left - a.left + b.width / 2}px ${b.top - a.top + b.height / 2}px`;
    surface.hidden = false;
    open = true;
    progress.set(1, { damping: 1, response: RESPONSE_OPEN });
    options.onOpen?.();
  }

  function closeSurface(velocity = 0): void {
    open = false;
    progress.set(0, { velocity, damping: 1, response: RESPONSE_OPEN });
  }

  let grabbed = 0;
  const travel = (): number => app.clientHeight * 0.8;
  for (const el of dragSurfaces) {
    cleanups.push(
      drag(el, {
        axis: "y",
        threshold: 10,
        onStart: () => {
          progress.stop();
          grabbed = progress.value;
        },
        onMove: (dy) => {
          let value = grabbed - dy / travel();
          if (value > 1) value = 1 + rubberband(value - 1, 1);
          if (value < 0) value = 0;
          progress.hold(value);
        },
        onEnd: (vy) => {
          const vp = -vy / travel();
          const target = nearest(progress.value + project(vp), [0, 1]);
          if (target === 0) closeSurface(vp);
          else {
            open = true;
            progress.set(1, { velocity: vp, damping: 0.85, response: 0.35 });
          }
        },
      }),
    );
  }
  scrim.addEventListener("click", () => closeSurface());
  apply(0);

  return {
    el: surface,
    get isOpen() {
      return open;
    },
    open: openSurface,
    close: () => closeSurface(),
    dispose: () => {
      progress.stop();
      for (const c of cleanups) c();
    },
  };
}
